import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function HomePage() {
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5egq6c7bv8ec1ogu8rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('訂閱成功！感謝您的訂閱');
        setEmail('');
      } else {
        setSubmitMessage('訂閱失敗，請稍後再試');
      }
    } catch (error) {
      setSubmitMessage('訂閱失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Promo Banner */}
      <div className="bg-gradient-to-r from-orange-500 via-pink-500 to-orange-500 text-white py-2 px-4 text-center text-sm overflow-hidden">
        <div className="animate-marquee whitespace-nowrap inline-block">
          🎉 限時優惠：新會員首月享7折優惠！立即註冊領取專屬優惠碼 | 📚 全新課程上線：高中微積分進階班現正招生
        </div>
      </div>

      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/46c4b423-33ec-4c59-b54c-70c60984f453.png" alt="MathLearn Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold text-gray-900">數學學習平台</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/materials" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">教材商城</Link>
              <a href="#courses" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">線上課程</a>
              <a href="#practice" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">練習平台</a>
              <a href="#membership" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">會員中心</a>
              <Link to="/homework" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">作業上傳</Link>
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-orange-500 transition-colors cursor-pointer"
              >
                <i className="ri-search-line text-xl"></i>
              </button>
              <button 
                onClick={() => setIsChatOpen(!isChatOpen)}
                className="relative w-10 h-10 flex items-center justify-center text-gray-700 hover:text-orange-500 transition-colors cursor-pointer"
              >
                <i className="ri-chat-3-line text-xl"></i>
                <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
              </button>
              {user ? (
                <Link to="/profile" className="hidden lg:block px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors font-medium whitespace-nowrap cursor-pointer">
                  會員中心
                </Link>
              ) : (
                <Link to="/login" className="hidden lg:block px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors font-medium whitespace-nowrap cursor-pointer">
                  登入 / 註冊
                </Link>
              )}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>

          {/* Search Bar */}
          {isSearchOpen && (
            <div className="mt-4 animate-fadeIn">
              <input 
                type="text" 
                placeholder="搜尋教材、課程..." 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
              />
            </div>
          )}

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-3 animate-fadeIn">
              <Link to="/materials" className="block text-gray-700 hover:text-orange-500 transition-colors font-medium">教材商城</Link>
              <a href="#courses" className="block text-gray-700 hover:text-orange-500 transition-colors font-medium">線上課程</a>
              <a href="#practice" className="block text-gray-700 hover:text-orange-500 transition-colors font-medium">練習平台</a>
              <a href="#membership" className="block text-gray-700 hover:text-orange-500 transition-colors font-medium">會員中心</a>
              <Link to="/homework" className="block text-gray-700 hover:text-orange-500 transition-colors font-medium">作業上傳</Link>
              {user ? (
                <Link to="/profile" className="w-full px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors font-medium whitespace-nowrap cursor-pointer block text-center">
                  會員中心
                </Link>
              ) : (
                <Link to="/login" className="w-full px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors font-medium whitespace-nowrap cursor-pointer block text-center">
                  登入 / 註冊
                </Link>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Chat Widget */}
      {isChatOpen && (
        <div className="fixed bottom-24 right-6 w-80 bg-white rounded-2xl shadow-2xl z-50 animate-slideUp">
          <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-customer-service-2-line text-xl"></i>
              </div>
              <div>
                <div className="font-semibold">即時客服</div>
                <div className="text-xs opacity-90">通常在5分鐘內回覆</div>
              </div>
            </div>
            <button onClick={() => setIsChatOpen(false)} className="cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="p-4 h-64 overflow-y-auto">
            <div className="bg-gray-100 rounded-lg p-3 mb-3">
              <p className="text-sm text-gray-700">您好！我是數學學習平台的客服助理，有什麼可以幫助您的嗎？</p>
            </div>
          </div>
          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <input 
                type="text" 
                placeholder="輸入訊息..." 
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
              />
              <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors cursor-pointer">
                <i className="ri-send-plane-fill"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/0 to-black/5"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-medium">
                🎓 專業數學學習平台
              </div>
              <h1 className="text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
                掌握數學<br />
                <span className="italic font-medium text-5xl lg:text-6xl text-gray-700">從基礎到進階</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                結合優質教材、互動課程與智能練習系統，打造最完整的數學學習體驗。無論您是學生、家長還是教師，我們都能提供最適合的學習方案。
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/materials" className="px-8 py-4 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-all font-semibold flex items-center space-x-2 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl">
                  <span>瀏覽教材</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a href="#practice" className="px-8 py-4 bg-white text-blue-900 border-2 border-blue-900 rounded-full hover:bg-blue-50 transition-all font-semibold flex items-center space-x-2 whitespace-nowrap cursor-pointer">
                  <span>免費試用</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/84d9541362770fc4da8f044a3fe57833.jpg" 
                  alt="學生使用平板學習" 
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-orange-400 rounded-full blur-3xl opacity-30"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-blue-400 rounded-full blur-3xl opacity-30"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="group relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-105 h-96">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700"></div>
              <img 
                src="https://readdy.ai/api/search-image?query=Mathematics%20textbooks%20and%20educational%20materials%20displayed%20on%20clean%20white%20surface%2C%20professional%20product%20photography%2C%20soft%20shadows%2C%20minimalist%20composition%20with%20geometric%20elements%2C%20modern%20educational%20design%2C%20high%20quality&width=400&height=500&seq=feat001&orientation=portrait" 
                alt="教材商城" 
                className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-8 left-8 text-white space-y-2">
                <h3 className="text-3xl font-bold">📚 精選教材</h3>
                <p className="text-white/90">超過500+專業教材</p>
                <Link to="/materials" className="mt-4 px-6 py-2 bg-white text-blue-900 rounded-full font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap cursor-pointer inline-block">
                  立即選購
                </Link>
              </div>
            </div>

            <div className="group relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-105 h-96">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-pink-500"></div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20teacher%20conducting%20online%20mathematics%20class%20on%20computer%20screen%2C%20video%20conference%20interface%2C%20engaging%20teaching%20moment%2C%20modern%20home%20office%20setup%2C%20warm%20professional%20lighting%2C%20educational%20technology&width=400&height=500&seq=feat002&orientation=portrait" 
                alt="線上課程" 
                className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-8 left-8 text-white space-y-2">
                <h3 className="text-3xl font-bold">🎥 互動課程</h3>
                <p className="text-white/90">名師線上即時教學</p>
                <button className="mt-4 px-6 py-2 bg-white text-orange-600 rounded-full font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap cursor-pointer">
                  探索課程
                </button>
              </div>
            </div>

            <div className="group relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-105 h-96">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-teal-500"></div>
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20mathematics%20practice%20platform%20interface%20on%20laptop%20screen%2C%20clean%20UI%20design%20with%20equations%20and%20graphs%2C%20student%20hands%20typing%2C%20bright%20workspace%2C%20educational%20software%2C%20professional%20photography&width=400&height=500&seq=feat003&orientation=portrait" 
                alt="練習平台" 
                className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-8 left-8 text-white space-y-2">
                <h3 className="text-3xl font-bold">✏️ 智能練習</h3>
                <p className="text-white/90">AI個人化學習路徑</p>
                <Link to="/homework" className="mt-4 px-6 py-2 bg-white text-green-700 rounded-full font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap cursor-pointer inline-block">
                  開始練習
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-pink-50 via-blue-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 space-y-8">
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                我們的<br />成就
              </h2>
              <div className="grid grid-cols-3 gap-8">
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-orange-500">50,000+</div>
                  <p className="text-gray-600 font-medium">活躍學生用戶</p>
                </div>
                <div className="space-y-2 border-l border-gray-300 pl-8">
                  <div className="text-5xl font-bold text-orange-500">500+</div>
                  <p className="text-gray-600 font-medium">專業教材資源</p>
                </div>
                <div className="space-y-2 border-l border-gray-300 pl-8">
                  <div className="text-5xl font-bold text-orange-500">200+</div>
                  <p className="text-gray-600 font-medium">五星好評課程</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 relative h-64 lg:h-full">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-48 h-48 bg-pink-300 rounded-full opacity-40 blur-2xl"></div>
                <div className="absolute w-40 h-40 bg-blue-300 rounded-full opacity-40 blur-2xl translate-x-12 translate-y-12"></div>
                <div className="absolute w-36 h-36 bg-orange-300 rounded-full opacity-40 blur-2xl -translate-x-8 translate-y-8"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products & Courses Section */}
      <section id="courses" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-4xl font-bold text-gray-900">熱門推薦</h2>
            <Link to="/materials" className="text-orange-500 hover:text-orange-600 font-semibold flex items-center space-x-2 cursor-pointer">
              <span>查看全部</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="group cursor-pointer">
              <div className="rounded-2xl overflow-hidden bg-pink-100 h-80 flex items-center justify-center mb-4 relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=High%20school%20algebra%20textbook%20cover%20design%2C%20clean%20modern%20layout%20with%20mathematical%20symbols%20and%20equations%2C%20soft%20pink%20background%2C%20professional%20educational%20publishing%20style%2C%20minimalist%20composition&width=300&height=400&seq=prod001&orientation=portrait" 
                  alt="高中代數教材" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">高中代數完整教材</h3>
                <div className="flex items-center justify-between">
                  <span className="text-orange-500 font-bold text-lg">HK$ 299</span>
                  <div className="flex items-center space-x-1 text-yellow-500 text-sm">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <span className="text-gray-600 ml-1">(4.9)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="rounded-2xl overflow-hidden bg-green-100 h-80 flex items-center justify-center mb-4 relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Geometry%20mathematics%20course%20thumbnail%20with%20geometric%20shapes%20and%20diagrams%2C%20mint%20green%20background%2C%20educational%20design%2C%20modern%20clean%20style%2C%20professional%20course%20material&width=300&height=400&seq=prod002&orientation=portrait" 
                  alt="幾何學精通課程" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">幾何學精通課程</h3>
                <div className="flex items-center justify-between">
                  <span className="text-orange-500 font-bold text-lg">NT$ 1,299</span>
                  <div className="flex items-center space-x-1 text-yellow-500 text-sm">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-half-fill"></i>
                    <span className="text-gray-600 ml-1">(4.7)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="rounded-2xl overflow-hidden bg-gray-100 h-80 flex items-center justify-center mb-4 relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Calculus%20mathematics%20textbook%20with%20graphs%20and%20formulas%2C%20light%20gray%20background%2C%20professional%20educational%20material%20design%2C%20modern%20minimalist%20style%2C%20high%20quality&width=300&height=400&seq=prod003&orientation=portrait" 
                  alt="微積分基礎班" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">微積分基礎班</h3>
                <div className="flex items-center justify-between">
                  <span className="text-orange-500 font-bold text-lg">NT$ 1,599</span>
                  <div className="flex items-center space-x-1 text-yellow-500 text-sm">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <span className="text-gray-600 ml-1">(5.0)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer">
              <div className="rounded-2xl overflow-hidden bg-amber-100 h-80 flex items-center justify-center mb-4 relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Statistics%20and%20probability%20course%20material%20with%20charts%20and%20data%20visualization%2C%20warm%20beige%20background%2C%20educational%20design%2C%20modern%20professional%20style&width=300&height=400&seq=prod004&orientation=portrait" 
                  alt="統計學入門" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">統計學入門</h3>
                <div className="flex items-center justify-between">
                  <span className="text-orange-500 font-bold text-lg">NT$ 999</span>
                  <div className="flex items-center space-x-1 text-yellow-500 text-sm">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-half-fill"></i>
                    <span className="text-gray-600 ml-1">(4.8)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
              <i className="ri-information-line text-blue-600"></i>
              <span>購買須知</span>
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start space-x-2">
                <i className="ri-check-line text-blue-600 mt-0.5"></i>
                <span>購買後可無限次下載與列印</span>
              </li>
              <li className="flex items-start space-x-2">
                <i className="ri-check-line text-blue-600 mt-0.5"></i>
                <span>如有疑問可預約線上教學指導</span>
              </li>
              <li className="flex items-start space-x-2">
                <i className="ri-check-line text-blue-600 mt-0.5"></i>
                <span>支援AI作業批改功能</span>
              </li>
            </ul>
          </div>
          <Link
            to="/homework"
            className="block w-full py-3 bg-green-600 text-white rounded-full text-center font-semibold hover:bg-green-700 transition-colors cursor-pointer"
          >
            <i className="ri-upload-line mr-2"></i>
            上傳作業獲取AI批改
          </Link>
        </div>
      </section>

      {/* Zoom Booking Section */}
      <section id="booking" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2">
            <div className="relative h-96 lg:h-auto">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20mathematics%20teacher%20conducting%20one-on-one%20online%20tutoring%20session%20via%20video%20call%2C%20friendly%20engaging%20teaching%20moment%2C%20modern%20home%20office%20with%20bookshelf%20background%2C%20warm%20professional%20lighting%2C%20educational%20technology%2C%20high%20quality%20photography&width=700&height=600&seq=zoom001&orientation=landscape" 
                alt="線上教學" 
                className="w-full h-full object-cover"
              />
              <div className="absolute top-8 left-8 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-gray-700">
                💻 一對一線上指導
              </div>
            </div>
            <div className="bg-gray-50 flex flex-col items-center justify-center p-12 lg:p-16 space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  遇到難題？<br />
                  預約專業教師指導
                </h2>
                <p className="text-gray-600 leading-relaxed max-w-md mx-auto">
                  購買教材後如有疑問，可預約專業教師進行一對一線上指導。透過Zoom平台，讓您在家也能享受面對面的教學體驗，快速解決學習困難。
                </p>
              </div>
              <a href="#booking-form" className="px-10 py-4 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer shadow-lg">
                <span>立即預約</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 space-y-6">
              <div className="text-sm font-medium text-gray-600">⭐ 學生好評</div>
              <blockquote className="text-4xl lg:text-5xl font-serif text-gray-900 leading-tight">
                <span className="text-6xl text-gray-400">"</span>
                這個平台讓我的數學成績進步了30分，練習系統特別有幫助！
              </blockquote>
              <div className="flex items-center space-x-3">
                <div>
                  <div className="font-bold text-gray-900">陳小明</div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>高中二年級學生</span>
                    <div className="flex items-center space-x-1 text-green-600">
                      <i className="ri-google-fill"></i>
                      <i className="ri-star-fill text-xs"></i>
                      <i className="ri-star-fill text-xs"></i>
                      <i className="ri-star-fill text-xs"></i>
                      <i className="ri-star-fill text-xs"></i>
                      <i className="ri-star-fill text-xs"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="relative w-full h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=Black%20and%20white%20portrait%20of%20Asian%20teenage%20student%20looking%20upward%20with%20hopeful%20expression%2C%20side%20angle%20shot%20from%20shoulders%20up%2C%20clean%20white%20background%2C%20professional%20photography%2C%20natural%20lighting%2C%20inspirational%20mood&width=400&height=500&seq=test001&orientation=portrait" 
                  alt="學生見證" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Platform Section */}
      <section id="practice" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">智能練習平台</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              AI驅動的個人化學習系統，根據您的學習進度自動調整難度，讓每一次練習都更有效率
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-brain-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI智能出題</h3>
              <p className="text-gray-600 leading-relaxed">
                根據您的學習狀況，智能生成最適合的練習題目，精準提升弱點領域
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">學習進度追蹤</h3>
              <p className="text-gray-600 leading-relaxed">
                詳細的學習數據分析，讓您清楚了解自己的進步軌跡與需要加強的部分
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-trophy-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">成就系統</h3>
              <p className="text-gray-600 leading-relaxed">
                完成挑戰獲得徽章與獎勵，讓學習變得更有趣，保持持續學習的動力
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section id="membership" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">選擇最適合您的方案</h2>
            <p className="text-lg text-gray-600">
              靈活的訂閱方案，滿足不同學習需求
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">基礎版</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900">NT$ 299</span>
                <span className="text-gray-600">/月</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">存取100+基礎教材</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">每月5小時練習平台</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">基礎學習報告</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">社群論壇支援</span>
                </li>
              </ul>
              <button className="w-full py-3 bg-gray-200 text-gray-900 rounded-full font-semibold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer">
                選擇方案
              </button>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-pink-500 rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all transform scale-105 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 bg-yellow-400 text-gray-900 rounded-full text-sm font-bold whitespace-nowrap">
                最受歡迎
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">進階版</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold text-white">NT$ 599</span>
                <span className="text-white/80">/月</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-white text-xl mt-1"></i>
                  <span className="text-white">存取全部500+教材</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-white text-xl mt-1"></i>
                  <span className="text-white">無限制練習平台使用</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-white text-xl mt-1"></i>
                  <span className="text-white">AI個人化學習路徑</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-white text-xl mt-1"></i>
                  <span className="text-white">每月2小時線上指導</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-white text-xl mt-1"></i>
                  <span className="text-white">詳細學習分析報告</span>
                </li>
              </ul>
              <button className="w-full py-3 bg-white text-orange-600 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                選擇方案
              </button>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">專業版</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900">NT$ 999</span>
                <span className="text-gray-600">/月</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">進階版所有功能</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">每月8小時線上指導</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">專屬學習顧問</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">優先客服支援</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                  <span className="text-gray-700">獨家進階課程</span>
                </li>
              </ul>
              <button className="w-full py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer">
                選擇方案
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">常見問題</h2>
            <p className="text-lg text-gray-600">
              快速找到您需要的答案
            </p>
          </div>
          <div className="space-y-4">
            {[
              {
                q: '如何開始使用數學學習平台？',
                a: '只需註冊帳號，選擇適合的訂閱方案，即可立即開始使用所有功能。我們提供7天免費試用，讓您充分體驗平台的完整功能。'
              },
              {
                q: '線上教學如何進行？',
                a: '購買教材後，您可以在會員中心預約線上指導時段。我們使用Zoom平台進行一對一教學，確保最佳的互動體驗。教師會根據您的學習進度提供個人化指導。'
              },
              {
                q: '可以隨時取消訂閱嗎？',
                a: '是的，您可以隨時在會員中心取消訂閱。取消後，您仍可使用服務至當期結束，不會立即失去存取權限。'
              },
              {
                q: '練習平台如何運作？',
                a: '我們的AI系統會根據您的答題表現，自動調整題目難度並推薦最適合的練習內容。系統會追蹤您的學習進度，提供詳細的分析報告。'
              },
              {
                q: '教材可以下載嗎？',
                a: '是的，所有購買的教材都可以下載PDF版本，方便您離線學習。部分互動式教材需要在線上平台使用以獲得最佳體驗。'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-2xl text-gray-600 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`}></i>
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed animate-fadeIn">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/46c4b423-33ec-4c59-b54c-70c60984f453.png" alt="Logo" className="h-8 w-8 object-contain" />
                <span className="text-xl font-bold">數學學習平台</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                讓數學學習變得簡單有趣
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">產品服務</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><Link to="/materials" className="hover:text-white transition-colors cursor-pointer">教材商城</Link></li>
                <li><a href="#courses" className="hover:text-white transition-colors cursor-pointer">線上課程</a></li>
                <li><a href="#practice" className="hover:text-white transition-colors cursor-pointer">練習平台</a></li>
                <li><Link to="/homework" className="hover:text-white transition-colors cursor-pointer">作業上傳</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">支援中心</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">使用指南</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">常見問題</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors cursor-pointer">聯絡我們</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">技術支援</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">關於我們</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">公司介紹</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">師資團隊</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">合作夥伴</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">最新消息</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div className="rounded-2xl overflow-hidden h-48 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=Students%20using%20digital%20learning%20platform%20on%20various%20devices%2C%20collaborative%20study%20environment%2C%20modern%20educational%20technology%2C%20warm%20inviting%20atmosphere%2C%20professional%20photography&width=400&height=250&seq=footer001&orientation=landscape" 
                  alt="學習情境" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-center lg:text-left">
                <h3 className="text-3xl font-serif leading-tight mb-2">
                  開始您的<br />
                  數學學習<br />
                  新旅程
                </h3>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-4">
                  訂閱電子報獲取最新課程資訊
                </p>
                <form onSubmit={handleNewsletterSubmit} className="flex space-x-2" data-readdy-form id="newsletter-form">
                  <input 
                    type="email" 
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="輸入您的電子郵件" 
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-white"
                    required
                  />
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-full font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <span>{isSubmitting ? '提交中...' : '訂閱'}</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </form>
                {submitMessage && (
                  <p className={`mt-2 text-sm ${submitMessage.includes('成功') ? 'text-green-400' : 'text-red-400'}`}>
                    {submitMessage}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © 2025 數學學習平台. 版權所有
            </div>
            <div className="flex items-center space-x-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                <i className="ri-youtube-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                <i className="ri-line-fill text-xl"></i>
              </a>
            </div>
            <div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
